import { Check, Copy } from 'lucide-react';
import { type DragEvent, type FC, type MouseEvent, memo, useEffect, useRef, useState } from 'react';

const COPIED_FEEDBACK_MS = 2000;

interface MeetingUrlProps {
  joinUrl: string;
}

const MeetingUrl: FC<MeetingUrlProps> = ({ joinUrl }) => {
  const [copied, setCopied] = useState(false);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (copiedTimerRef.current) {
        clearTimeout(copiedTimerRef.current);
      }
    };
  }, []);

  const handleCopy = async (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(joinUrl);
      setCopied(true);
      if (copiedTimerRef.current) {
        clearTimeout(copiedTimerRef.current);
      }
      copiedTimerRef.current = setTimeout(() => {
        setCopied(false);
        copiedTimerRef.current = null;
      }, COPIED_FEEDBACK_MS);
    } catch (err) {
      console.error('Failed to copy meeting URL', err);
    }
  };

  const handleDragStart = (event: DragEvent<HTMLDivElement>) => {
    event.stopPropagation();
    event.dataTransfer.setData('text/uri-list', joinUrl);
    event.dataTransfer.setData('text/plain', joinUrl);
    event.dataTransfer.effectAllowed = 'copy';
  };

  return (
    <div className="personal-meetings-modal__meeting-url-row">
      {/* biome-ignore lint/a11y/noStaticElementInteractions: draggable URL text has no click behavior, only drag */}
      <div
        className="personal-meetings-modal__meeting-url"
        draggable
        onDragStart={handleDragStart}
        title={joinUrl}
      >
        {joinUrl}
      </div>
      <button
        type="button"
        className="personal-meetings-modal__meeting-copy-button"
        onClick={handleCopy}
        aria-label={copied ? 'Meeting URL copied' : 'Copy meeting URL'}
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
      </button>
    </div>
  );
};

export default memo(MeetingUrl);
